"use client";

import { useEffect, useRef } from "react";
import { useMotionMode } from "@/context/MotionContext";
import { Magnetic } from "./Magnetic";

interface StatBlockProps {
  number: number;
  label: string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  delay?: number;
  onClick?: () => void;
}

export function StatBlock({
  number,
  label,
  prefix = "",
  suffix = "",
  decimals = 0,
  delay = 0,
  onClick,
}: StatBlockProps) {
  const ref = useRef<HTMLDivElement>(null);
  const numberRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef({ value: 0 });
  const { canAnimate } = useMotionMode();

  useEffect(() => {
    const el = ref.current;
    const numEl = numberRef.current;
    if (!el || !numEl) return;

    // Skip animations for reduced motion - show final value immediately
    if (!canAnimate) {
      el.style.opacity = "1";
      el.style.transform = "none";
      numEl.textContent = prefix + number.toFixed(decimals) + suffix;
      return;
    }

    // Set initial state
    el.style.opacity = "0";
    el.style.transform = "translateY(30px) scale(0.9)";

    let hasAnimated = false;

    const runAnimation = async () => {
      if (hasAnimated) return;
      hasAnimated = true;

      const gsap = (await import("gsap")).default;

      // Animate container in
      gsap.to(el, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.5,
        delay: delay / 1000,
        ease: "power3.out",
      });

      // Animate number count
      gsap.to(valueRef.current, {
        value: number,
        duration: 0.8,
        delay: delay / 1000,
        ease: "power2.out",
        onUpdate: () => {
          numEl.textContent =
            prefix + valueRef.current.value.toFixed(decimals) + suffix;
        },
      });
    };

    // Use Intersection Observer - works reliably on mobile
    // rootMargin creates a trigger line 100px above bottom of viewport
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          runAnimation();
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: "0px 0px -100px 0px" }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [number, prefix, suffix, decimals, delay, canAnimate]);

  return (
    <div ref={ref} className="text-center" data-cursor-ignore>
      <Magnetic strength={0.15} tiltStrength={5} onClick={onClick}>
        <div
          ref={numberRef}
          className={`text-[clamp(2.5rem,6vw,4rem)] font-bold gradient-text mb-2 font-headline tabular-nums ${onClick ? "cursor-pointer" : ""}`}
        >
          0{suffix}
        </div>
        <div className="stat-label-center text-[0.75rem] text-[rgba(var(--foreground-rgb),0.5)] uppercase tracking-[0.15em] font-bold max-w-[120px] mx-auto">
          {label}
        </div>
      </Magnetic>
    </div>
  );
}
